import React, { useState, useMemo, useRef, useEffect } from 'react';
import { View, Text, ScrollView, Pressable, Share, Modal, ActivityIndicator, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import {
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  MapPin,
  Calendar,
  Users,
  Globe,
  Linkedin,
  FolderOpen,
  Star,
  X,
  Share2,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Newspaper,
  ExternalLink,
  Clock,
  Zap,
  Megaphone,
  UserPlus,
  Rocket,
  Info,
  Mail,
  Send,
  Eye,
  EyeOff,
  FileSearch,
  KeyRound,
  RotateCcw,
} from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { labelColor, scoreToLabel, capitalizeLabel } from '@/lib/scoring/score-display';
import { IRSScorePill } from '@/components/score/atoms/IRSScorePill';
import { openExternalUrl } from '@/lib/linking';
import { useThemeColors } from '@/lib/useThemeColors';
import { track, useTrackScreen } from '@/lib/analytics';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { useProfileStore } from '@/lib/state/profile-store';
import { StartupUpdate } from '@/lib/state/inverio-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import type { StartupSummary } from '@/lib/supabase/edge-functions';
import { inviteFounderToClaim } from '@/lib/supabase/edge-functions';
import { handleError } from '@/lib/errors';

/** Extended startup shape with optional fields that exist on some StartupSummary objects
 *  (e.g. those converted from StartupProfile via toStartupSummary in mock-data). */
type StartupData = StartupSummary & {
  updates?: StartupUpdate[];
  targetCustomer?: string;
  founderEmail?: string;
  isSerialFounder?: boolean;
  previousVentures?: string[];
};
import { formatCurrency } from '@/lib/utils/format';
import { SectionDivider } from '@/components/SectionDivider';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { InviteFounderModal } from '@/components/investor/InviteFounderModal';
import { useCompanyDirectoryItem } from '@/lib/supabase/company-directory';
import { useCompanyPublicCard } from '@/lib/company/public-card';
import { sealedDetailCandidate } from '@/lib/dealflow/sealed-deals';
import { SealedCompanyDetail } from '@/components/company/SealedCompanyDetail';
import { useRaisingStartups, useAddRaisingStartupToDealflow, usePortfolioFromSupabase } from '@/lib/supabase/investor-queries';
import { useAuth } from '@/lib/supabase';
import { useAlert } from '@/components/AlertDialog';
import { MessageFounderButton } from '@/components/messaging/MessageFounderButton';
import { useMyDataRoomGrant, useMyDataRoomAccessRequests } from '@/lib/supabase/api';
import { useFlag } from '@/lib/feature-flags';
import { StrategicAnalysisEntry } from '@/components/strategic-analysis/StrategicAnalysisEntry';
import { useStrategicEntryState } from '@/lib/strategic-analysis/use-strategic-analysis';
import { requestStateFor } from '@/lib/dd/dd-entry';
import { offeredActions, actionTarget, appearsOn, awaitingFounderAnswer, dataRoomOpen, isRaisingEntryStage, type DealAction, type DealStage } from '@/lib/dealflow/stage';
import { dataRoomCardState, cardDrawsOwnCta } from '@/lib/dealflow/data-room-cta';
import { useRequestDataRoomAccess } from '@/lib/dealflow/deal-actions';
import { useChangeStage } from '@/lib/dealflow/use-change-stage';
import { CARD_ACTION_PRESENTATION, type CardActionIcon } from '@/components/investor/dealflow-card-actions';

// Resolves a `CardActionIcon` name (data) to the `LucideIcon` component
// (presentation) `CardActionButton` renders. Kept local to this file — see
// `dealflow-card-actions.ts` for why the presentation map itself cannot hold
// components.
const CARD_ACTION_ICONS: Record<CardActionIcon, LucideIcon> = {
  'chevron-right': ChevronRight,
  eye: Eye,
  'file-search': FileSearch,
  'key-round': KeyRound,
  'rotate-ccw': RotateCcw,
  star: Star,
};
import { CardActionButton } from '@/components/investor/CardActionButton';

const stageLabels: Record<string, string> = {
  'pre-seed': 'Pre-Seed',
  'seed': 'Seed',
  'series-a': 'Series A',
};

/**
 * Confirmation copy for stage-changing actions that keep the deal on THIS
 * screen (see `appearsOn` check in the action bar below). Actions that move
 * the deal off the dealflow surface (reject, monitor) navigate back instead
 * of showing a toast — same as the pre-derivation Pass behavior.
 */
const STATUS_CHANGE_FEEDBACK: Partial<Record<DealAction, { title: string; message: string }>> = {
  'mark-interested': { title: 'Marked as Interested', message: 'This startup has been added to your interested list.' },
  // No 'request-access' entry: that action is not a plain stage change and
  // never reaches this map — `handleRequestAccess` owns its own confirmation
  // and toast, because it has a request row to write before there is anything
  // to celebrate.
  're-engage': { title: 'Re-engaged', message: 'This startup is back in your active pipeline.' },
};

export default function StartupDetailScreen() {
  useTrackScreen('StartupDetail');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const insets = useSafeAreaInsets();
  const { error, success, confirm } = useAlert();
  const { id, directoryId, source } = useLocalSearchParams<{ id: string; directoryId?: string; source?: string }>();

  // Fetch directory entry if viewing from company directory
  const isDirectorySource = source === 'directory';
  const { data: directoryEntry, isLoading: isDirectoryLoading } = useCompanyDirectoryItem(isDirectorySource ? (directoryId ?? id) : undefined);

  // Track startup detail viewed on mount (ref-guarded)
  const detailTracked = useRef(false);
  useEffect(() => {
    if (detailTracked.current || !id) return;
    detailTracked.current = true;
    track('startup_detail_viewed', { startupId: id, source: source ?? 'dealflow' });
  }, [id, source]);
  const [isMasked, setIsMasked] = useState(true);
  // Session-local "already requested" flag for immediate honest feedback after a
  // real request succeeds (the button reflects it instead of re-offering Request).
  const [accessRequested, setAccessRequested] = useState(false);
  const { requestDataRoomAccess, isRequesting } = useRequestDataRoomAccess();
  const dealflow = useDealflowStore((s) => s.dealflow);
  // Real portfolio, not the demo-only store slice that used to back this —
  // it was always empty in production, so a portfolio company opened from
  // anywhere but dealflow rendered "not found".
  // The auth user. Named for its first consumer, but it is the plain auth
  // identity: also what authors the chat message announcing an access request,
  // because the messages RLS INSERT policy checks `sender_id = auth.uid()`.
  const { user: portfolioAuthUser } = useAuth();
  const { data: portfolio = [] } = usePortfolioFromSupabase(portfolioAuthUser?.id);
  const investorProfile = useProfileStore((s) => s.investorProfile);
  // Durable: store (snappy) PLUS the DB write. This screen used to write the
  // store ONLY, so every stage change it made was silently reverted by the next
  // `mergeServerDealflow` — and for `request-access` that meant the founder's
  // approval found the deal still at `interested` and moved nothing.
  const changeStage = useChangeStage();
  // Raising-feed lookup source: feed items are NOT in the persisted store
  // (cache hit when arriving from the dealflow "Raising Now" section).
  const { data: raisingFeed } = useRaisingStartups();
  const addRaisingToDealflow = useAddRaisingStartupToDealflow();

  // Find the startup in dealflow first (by deal id or startup id)
  let deal = dealflow.find((d) => d.id === id);
  if (!deal) {
    deal = dealflow.find((d) => d.startup.id === id);
  }

  // If not in dealflow, check portfolio
  const portfolioCompany = !deal ? portfolio.find((p) => p.startup.id === id) : null;

  // Finally, fall back to the raising-startups feed (id = startup_profiles id)
  if (!deal && !portfolioCompany) {
    deal = raisingFeed?.find((d) => d.id === id || d.startup.id === id);
  }
  const isRaisingFeedItem = deal?.source === 'raising';

  // Build startup object from directory entry when viewing from directory
  const directoryStartup: StartupData | null = useMemo(() => {
    if (!directoryEntry || !isDirectorySource) return null;
    return {
      id: directoryEntry.id,
      company: {
        name: directoryEntry.name,
        tagline: directoryEntry.tagline ?? '',
        description: directoryEntry.description ?? '',
        industry: directoryEntry.industry ?? 'Other',
        // JUSTIFIED as any: DB stage enum may not match frontend StartupStage union
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        stage: (directoryEntry.stage as any) ?? 'pre-seed',
        location: [directoryEntry.city, directoryEntry.canton].filter(Boolean).join(', '),
        founded: directoryEntry.founded_year ?? 0,
        website: directoryEntry.website ?? '',
      },
      funding: {
        isActivelyRaising: false,
        currentRaise: null,
        history: [],
      },
      team: {
        size: directoryEntry.team_size ?? 0,
        people: directoryEntry.linkedin ? [{ name: '', role: '', relationship: 'founder', linkedIn: directoryEntry.linkedin }] : [],
        compositionSummary: '',
      },
      traction: { metrics: [] },
      market: { tam: 0, sam: 0, som: 0, tamSource: '', growthRate: 0, competitors: [] },
      problemSolution: { problem: '', solution: '' },
      financials: { unitEconomics: {}, projections: [] },
      product: [],
      gtm: { channels: [], businessModel: '', pricingModel: '', distributionSummary: '' },
      socialProof: { investorNames: [], partnerships: [], awards: [] },
      deckQuality: { structureScore: 0, narrativeFlow: '', designQuality: '', completeness: 0, missingSections: [] },
    };
  }, [directoryEntry, isDirectorySource]);

  // Determine startup and whether it's from portfolio
  const isPortfolioView = !!portfolioCompany;
  const startup: StartupData | undefined = (deal?.startup || portfolioCompany?.startup || directoryStartup) as StartupData | undefined;

  /**
   * The `startup_profiles.id` for this screen, or null if there isn't one.
   *
   * NEVER use the `id` route param for this. Callers populate it from three
   * different id spaces — `dealflow.id` (discover-startups, watchlist,
   * deal-comparison, the dealflow card push), `startup_profiles.id`
   * (notifications, the raising-feed push, investor-dashboard), and
   * `company_directory.id` (source=directory). The lookup above has to try
   * `d.id === id` AND `d.startup.id === id` for exactly that reason.
   *
   * `startup.id` is no good either: `directoryStartup` sets it to
   * `directoryEntry.id`, a company_directory id.
   *
   * So resolve from the ENTITY. `startup_profile_id` is nullable — an unclaimed
   * directory company has no profile to ask, and callers must handle null
   * rather than write it. Writing the unresolved param is what produced
   * INVERIO-APP-W: 23503, "Key is not present in table startup_profiles".
   */
  const startupProfileId: string | null =
    deal?.startup?.id ?? portfolioCompany?.startup?.id ?? directoryEntry?.startup_profile_id ?? null;

  // 119-07: a SEALED company (closed / acquired / bankrupt) has no readable
  // profile — its dealflow row arrives with a NULL embed, and a cold link finds
  // nothing at all. Ask the card RPC (a card exists only for a sealed company)
  // and render the public record instead of an empty "Unknown" screen.
  const sealedCandidate = sealedDetailCandidate({ deal, hasStartup: !!startup && !deal?.profileMissing, routeId: id });
  const sealed = useCompanyPublicCard(sealedCandidate);

  // The founder's own row in `data_room_access` — the authorisation of record,
  // and what `document-signed-url` and `dd-chat` actually check.
  const { data: dataRoomGranted = false } = useMyDataRoomGrant(deal?.startup?.id);

  // One kill switch, defaulting closed. The server enforces it again.
  const strategicAnalysisEnabled = useFlag('strategic_analyses', { default: false });
  // `canEdit: false` is what keeps readiness and sourcesChanged out of an
  // investor's card — they describe founder-private material — and it also
  // stops the founder-only draft endpoint being called at all.
  const strategicEntry = useStrategicEntryState(startupProfileId ?? undefined, {
    canEdit: false,
    enabled: strategicAnalysisEnabled,
  });

  /**
   * Has this investor already asked, and what came back?
   *
   * Read from their own request rows, NOT from the stage. This screen had no
   * DB-backed answer at all — only the session-local `accessRequested` flag,
   * which resets on every remount — so re-entering the screen re-offered a
   * request that RLS refuses (INVERIO-APP-P/-Q/-X/-Y).
   */
  const { data: myAccessRequests = [] } = useMyDataRoomAccessRequests(investorProfile?.id);
  const requestState = requestStateFor(startupProfileId ?? undefined, myAccessRequests);

  // Are the documents open to us right now?
  //
  // A UNION, and each half earns its place. The STAGE answers immediately and
  // locally — `diligence` and `invested` both imply a live grant, because the
  // grant is written and revoked with the stage (`respond_to_data_room_request`
  // on the way in, `conclude_dd_report` on the way out). The GRANT answers the
  // one case no stage records: a founder revoking by hand, or a deal rejected
  // without ever concluding a DD, where the grant outlives the pipeline label.
  //
  // Ordering matters for the first paint: `useMyDataRoomGrant` is async, so
  // during that window the stage alone carries it. Reading the grant FIRST and
  // the stage not at all would flash a locked data room at every investor in
  // diligence on every mount.
  // A profile-missing deal (sealed, 119-07) never shows its documents open:
  // a close revokes every grant and the data room is sealed.
  const documentsOpen = !deal?.profileMissing && ((deal ? dataRoomOpen(deal.status) : false) || dataRoomGranted);

  // Determine investor access level for update visibility
  const hasDataRoomAccess = documentsOpen || isPortfolioView;
  const hasInvested = isPortfolioView;

  // State for viewing update details
  const [selectedUpdate, setSelectedUpdate] = useState<StartupUpdate | null>(null);

  // Score details are available to all investor tiers — no subscription gate.
  const handleViewScore = () => {
    if (deal) {
      router.push(`/(investor)/investor-score-detail?dealId=${deal.id}`);
    }
  };

  // Check if this was analyzed from a pitch deck
  const isAIAnalyzed = useMemo(() => {
    if (!deal?.notes) return false;
    return deal.notes.includes('Analyzed from');
  }, [deal]);

  // Extract confidence from notes if available (from pitch deck analysis)
  const extractionConfidence = useMemo(() => {
    if (!deal?.notes) return null;
    // Check if this was analyzed from a pitch deck and extract confidence
    if (deal.notes.includes('Confidence:')) {
      const match = deal.notes.match(/Confidence:\s*(\d+)%/);
      if (match) {
        return parseInt(match[1], 10);
      }
    }
    return null;
  }, [deal]);

  // Check if this is an unclaimed startup (analyzed from deck or from directory, no founder connected)
  const isUnclaimed = useMemo(() => {
    if (!startup) return false;
    return isDirectorySource || isAIAnalyzed;
  }, [startup, isDirectorySource, isAIAnalyzed]);

  // State for invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);

  // Dynamic breadcrumbs
  const breadcrumbs: Breadcrumb[] = useMemo(() => {
    if (isDirectorySource) {
      return [
        { label: 'Directory', href: '/company-directory' },
        { label: startup?.company.name || 'Startup' },
      ];
    }
    if (isPortfolioView) {
      return [
        { label: 'Portfolio', href: '/(investor)/portfolio' },
        { label: startup?.company.name || 'Startup' },
      ];
    }
    return [
      { label: 'Dealflow', href: '/(investor)/dealflow' },
      { label: startup?.company.name || 'Startup' },
    ];
  }, [startup?.company.name, isPortfolioView, isDirectorySource]);

  // Filter updates based on investor access level
  const visibleUpdates = startup?.updates?.filter((update) => {
    // Public updates are always visible
    if (update.visibility === 'public') return true;
    // Data room updates require data room access or being an investor
    if (update.visibility === 'data_room') return hasDataRoomAccess;
    // Investors only updates require having invested
    if (update.visibility === 'investors_only') return hasInvested;
    return false;
  }) || [];

  // Get icon for update type
  const getUpdateIcon = (type: StartupUpdate['type']) => {
    switch (type) {
      case 'milestone':
        return <Star size={18} color={colors.success} />;
      case 'funding':
        return <DollarSign size={18} color={colors.success} />;
      case 'traction':
        return <TrendingUp size={18} color={colors.warning} />;
      case 'team':
        return <UserPlus size={18} color={colors.primary[600]} />;
      case 'product':
        return <Rocket size={18} color={colors.primary[600]} />;
      case 'announcement':
        return <Megaphone size={18} color={colors.primary[600]} />;
      default:
        return <Newspaper size={18} color={colors.text.tertiary} />;
    }
  };

  // Get background color for update type
  const getUpdateBgColor = (type: StartupUpdate['type']) => {
    switch (type) {
      case 'milestone':
      case 'funding':
        return colors.traffic.greenLight;
      case 'traction':
        return colors.amber.light;
      default:
        return colors.primary[50];
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Build real metrics from extracted data (must be above early returns — rules of hooks)
  const realMetrics = useMemo(() => {
    if (!startup) return [];
    const items: { label: string; value: string; icon: typeof TrendingUp }[] = [];
    const mrr = startup.traction.metrics.find(m => m.name === 'MRR')?.value ?? 0;
    const users = startup.traction.metrics.find(m => m.name === 'Users')?.value ?? 0;
    const growth = startup.traction.metrics.find(m => m.name === 'Growth')?.value ?? 0;
    const tam = startup.market.tam;
    if (mrr > 0) {
      items.push({ label: 'Monthly Revenue', value: formatCurrency(mrr), icon: DollarSign });
    }
    if (users > 0) {
      items.push({ label: 'Users', value: users.toLocaleString(), icon: Users });
    }
    if (growth > 0) {
      items.push({ label: 'Growth Rate', value: `${growth}%`, icon: TrendingUp });
    }
    if (tam && tam > 0) {
      items.push({ label: 'Market Size (TAM)', value: formatCurrency(tam), icon: Globe });
    }
    return items;
  }, [startup]);

  /**
   * Actions are DERIVED from the stage — do not add a button here, add it to
   * stage.ts's ACTIONS table, or it will be offered at the wrong stage.
   * 'view' is dropped: it exists so a LIST row can navigate to this very
   * screen, and is meaningless once you're already on it.
   * (Must sit above the early returns below — rules of hooks.)
   */
  const dealActions: readonly DealAction[] = useMemo(
    () =>
      deal
        ? offeredActions(deal.status).filter((action) => {
            if (action === 'view') return false;
            // An UNCLAIMED profile has no founder to answer, so a request row
            // written against it can never be responded to — it would notify
            // nobody (api.ts finds no owner) and strand the deal at
            // `awaiting_access`, which offers no way back. The Data Room card
            // below then renders the disabled "Awaiting Founder" state instead,
            // which is what the screen showed before this action existed.
            if (action === 'request-access') return !isUnclaimed;
            return true;
          })
        : [],
    [deal, isUnclaimed],
  );

  if (sealed.card) {
    return (
      <SealedCompanyDetail
        card={sealed.card}
        deal={deal && deal.source !== 'raising' ? { status: deal.status, notes: deal.notes } : null}
      />
    );
  }

  // Asking whether it is sealed: wait, rather than flash "Unknown" or "not found".
  if (sealedCandidate && sealed.isLoading) {
    return (
      <View
        testID="startup-detail-sealed-loading"
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.surface.secondary }}
      >
        <ActivityIndicator size="large" color={colors.primary[600]} />
        <Text numberOfLines={1} className="text-sm mt-3" style={{ color: colors.text.secondary }}>Loading company...</Text>
      </View>
    );
  }

  if (!startup && isDirectoryLoading) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
        <ActivityIndicator size="large" color={colors.primary[600]} />
        <Text className="text-sm mt-3" style={{ color: colors.text.secondary }}>Loading company...</Text>
      </View>
    );
  }

  if (!startup) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.primary }}>
        <Text style={{ color: colors.text.primary }}>Startup not found</Text>
        <Pressable
          onPress={() => router.back()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
          className="mt-4 px-6 py-3 rounded-xl"
          style={{ backgroundColor: colors.primary[600] }}
        >
          <Text className="text-white font-medium">Go Back</Text>
        </Pressable>
      </View>
    );
  }

  /**
   * This screen used to render TWO buttons both labelled "Request Access" — the
   * derived action-bar one and the Data Room card's — doing different things.
   * They are one action now, and only ONE of them is drawn.
   *
   * The action bar is the canonical home (actions derive from stage.ts), so the
   * card's CTA yields to it when the stage offers `request-access`, and instead
   * reports where the request has got to.
   */
  const requestOfferedInActionBar = dealActions.includes('request-access');
  // `accessRequested` stays: it is optimistic local state covering the window
  // before the stage round-trips, not a second store of the stage.
  const isAwaitingFounder =
    (deal ? awaitingFounderAnswer(deal.status) : false) ||
    accessRequested ||
    requestState === 'pending';
  /**
   * The Data Room card's position, decided in ONE place
   * (`lib/dealflow/data-room-cta.ts`) instead of by three inline ternary chains
   * that could disagree. It is a leaf module so this decision is testable
   * without rendering — which is the only way to prove the duplicate button
   * stopped drawing, RTL being unloadable on this branch.
   */
  const dataRoomState = dataRoomCardState({
    documentsOpen,
    unclaimed: isUnclaimed,
    awaitingFounder: isAwaitingFounder,
    requestRejected: requestState === 'rejected',
    requestOfferedInActionBar,
  });

  const handleShare = async () => {
    try {
      const scoreText = deal ? `\n\nInvestment Readiness Score: ${deal.readinessScore}/100` : '';
      const portfolioText = isPortfolioView && portfolioCompany ? `\n\nPortfolio Company - ${portfolioCompany.returns ? `${portfolioCompany.returns}x returns` : 'Active investment'}` : '';
      await Share.share({
        message: `Check out ${startup.company.name} on Inverio!\n\n${startup.company.tagline}${scoreText}${portfolioText}`,
      });
    } catch (error) {
      // Share cancelled or failed
    }
  };

  /**
   * THE single "request access" path on this screen.
   *
   * Both entry points that existed here did half the job each: the derived
   * action-bar button wrote only the stage (so no founder was ever asked and
   * the deal stranded at `awaiting_access`, which offers no re-request), while
   * the Data Room card's button wrote only the request row (so the stage stayed
   * `interested` and the founder's approval silently matched zero rows). They
   * now share this one handler, which does both via
   * `useRequestDataRoomAccess` — row first, stage only if it succeeds.
   */
  const handleRequestAccess = async () => {
    // Guard: needs a resolved investor profile + a real startup id to persist the
    // request. Never show success without one (that was the fake-success bug).
    if (!investorProfile?.id || !startupProfileId) {
      error('Cannot Request Access', 'Your profile is still loading. Please try again in a moment.');
      return;
    }
    if (!(await confirm('Request Data Room Access', `Send a request to ${startup.company.name} for access to their data room?`))) {
      return;
    }
    try {
      // Real persistence: upserts a pending data_room_access_requests row,
      // notifies the founder, THEN moves the deal to `awaiting_access`.
      // Success is shown ONLY after both resolve.
      await requestDataRoomAccess({
        // A raising-feed item has no dealflow row, so there is no stage to
        // move — the hook skips the stage write on null.
        dealId: isRaisingFeedItem ? null : (deal?.id ?? null),
        startupId: startupProfileId,
        investorProfileId: investorProfile.id,
        investorUserId: portfolioAuthUser?.id,
      });
      setAccessRequested(true);
      // Report the startup we actually wrote, not the route param — otherwise
      // the event is keyed on a dealflow id for half the entry points.
      track('dealflow_status_changed', { startupId: startupProfileId, newStatus: 'awaiting_access' });
      success('Request Sent', 'The founder will be asked to open their data room. Diligence starts once they approve.');
    } catch (err) {
      // The stage is NOT moved when this throws, so the button stays offered.
      const { message, isCancelled } = handleError(err, { context: 'StartupDetail.handleRequestAccess' });
      if (!isCancelled) error('Request Failed', message);
    }
  };

  /**
   * Applies a stage transition triggered from the derived action bar below.
   * A raising-feed startup has no dealflow row yet, so it creates one via
   * `useAddRaisingStartupToDealflow` — equivalent to a deal arriving at
   * `screening` and being acted on immediately, so the legal targets are
   * exactly `screening`'s transitions (interested, rejected, monitoring —
   * see `isRaisingEntryStage` above and the identical check in
   * `(investor)/dealflow.tsx`'s `handleRaisingStatus`). Any other target is a
   * no-op for it; a real pipeline item accepts every `DealStage`.
   */
  const setDealStatus = (status: DealStage): boolean => {
    if (!deal) return false;
    if (isRaisingFeedItem) {
      if (!investorProfile?.id) return false;
      if (!isRaisingEntryStage(status)) return false;
      addRaisingToDealflow.mutate({
        investorProfileId: investorProfile.id,
        feedItem: deal,
        status,
      });
      return true;
    }
    // `deal.status` is where the card is right now, and it is also where a
    // rejected server write puts it back — see `lib/dealflow/use-change-stage.ts`.
    changeStage(deal.id, deal.status, status);
    return true;
  };

  /**
   * Handles a tap on a derived action-bar button. Navigation-only actions
   * ('open-diligence') skip the status write entirely; everything else has a
   * `DealStage` target from `actionTarget`. 'view' is never offered here (see
   * the action bar below), so it never reaches this handler.
   */
  const handleDealAction = (action: DealAction) => {
    if (!deal) return;
    if (action === 'open-diligence') {
      track('data_room_accessed', { startupId: id });
      router.push(id ? `/duediligence/${id}` : '/(investor)/duediligence');
      return;
    }
    // NOT a plain stage change: it must write the founder's request row too, or
    // the deal strands at `awaiting_access` with nobody asked. One handler, one
    // path — the Data Room card's CTA calls the same function.
    if (action === 'request-access') {
      void handleRequestAccess();
      return;
    }
    const target = actionTarget(action);
    if (target === null) return;
    if (!setDealStatus(target)) return;
    track('dealflow_status_changed', { startupId: deal.id, newStatus: target });
    if (appearsOn(target, 'dealflow')) {
      const feedback = STATUS_CHANGE_FEEDBACK[action];
      if (feedback) success(feedback.title, feedback.message);
    } else {
      // The deal left the dealflow surface (rejected, monitoring) — there is
      // nothing left to show on this screen, same as the old Pass behavior.
      router.back();
    }
  };

  // Handle inviting founder to claim their startup page
  const handleInviteFounder = async () => {
    if (!inviteEmail.trim()) {
      error('Email Required', 'Please enter the founder\'s email address.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      error('Invalid Email', 'Please enter a valid email address.');
      return;
    }

    const trimmed = inviteEmail.trim();
    setInviteSending(true);

    try {
      const { error: inviteError } = await inviteFounderToClaim({
        email: trimmed,
        companyName: startup?.company.name ?? 'this company',
        startupId: startup?.id,
      });
      if (inviteError) {
        const { message, isCancelled } = handleError(new Error(inviteError), { context: 'StartupDetail.inviteFounder' });
        if (!isCancelled) error('Error', message);
        return;
      }

      setShowInviteModal(false);
      setInviteEmail('');
      success('Invitation Sent!', `We've emailed ${trimmed} an invitation to claim ${startup?.company.name ?? 'their company'}'s profile on Inverio.`);
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'StartupDetail.inviteFounder' });
      if (!isCancelled) error('Error', message);
    } finally {
      setInviteSending(false);
    }
  };

  // Has real content to show (from AI extraction or profile data)
  const founderPerson = startup?.team.people.find(p => p.relationship === 'founder');
  const hasDescription = !!(startup?.problemSolution.problem || startup?.problemSolution.solution || startup?.company.description || startup?.targetCustomer);
  const hasFounderInfo = !!(founderPerson?.name || startup?.founderEmail || founderPerson?.linkedIn || startup?.company.website);
  const hasCompetitors = !!(startup?.market.competitors && startup.market.competitors.length > 0);

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
        {/* Header - Mobile only */}
        {phoneShell && (
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="px-5 pt-4 pb-4"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <View className="flex-row items-center justify-between">
              <AnimatedPressable
                onPress={() => router.back()}
                accessibilityRole="button"
                accessibilityLabel="Go back"
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </AnimatedPressable>
              <View className="flex-row gap-2">
                <AnimatedPressable
                  onPress={handleShare}
                  accessibilityRole="button"
                  accessibilityLabel={`Share ${startup.company.name}`}
                  className="w-10 h-10 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Share2 size={20} color={colors.text.secondary} />
                </AnimatedPressable>
              </View>
            </View>
          </Animated.View>
        )}

          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Company Header */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mb-6"
            >
              <LinearGradient
                colors={[colors.primary[600], colors.primary[500]]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{ borderRadius: 24, padding: 24 }}
              >
                <View className="flex-row items-start justify-between mb-4">
                  {/*
                    Falls back to the logo carried on the startup summary.
                    This used to read ONLY `directoryEntry`, which is fetched
                    just when `source === 'directory'` — so arriving from
                    dealflow showed initials even for a company whose logo had
                    been extracted, re-hosted and was being served.
                  */}
                  {(directoryEntry?.logo_url ?? startup.company.logoUrl) ? (
                    <Image
                      source={{ uri: (directoryEntry?.logo_url ?? startup.company.logoUrl) as string }}
                      style={{ width: 64, height: 64, borderRadius: 16, backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                      resizeMode="contain"
                    />
                  ) : (
                    <View
                      className="w-16 h-16 rounded-2xl items-center justify-center"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.25)' }}
                    >
                      <Text className="text-white text-xl font-bold">
                        {startup.company.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </Text>
                    </View>
                  )}
                  {deal && (deal.scoringFailed || deal.readinessScore == null) ? (
                    // No score to show — either the analysis failed
                    // (`scoringFailed`) or the startup has no score event at all
                    // (`readinessScore == null`). Both must render the same
                    // honest state: publishing a number nobody computed is
                    // worse than a gap, and 0 renders as 'critical' — the
                    // harshest verdict — for a company simply never scored.
                    // Tapping re-runs / requests scoring.
                    <AnimatedPressable
                      onPress={handleViewScore}
                      accessibilityRole="button"
                      accessibilityLabel="Not scored — scoring did not complete. Tap to retry."
                      className="px-3 py-1.5 rounded-full flex-row items-center gap-1.5"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                    >
                      <TrendingUp size={14} color={colors.text.secondary} />
                      <Text style={{ color: colors.text.secondary }} className="font-semibold text-xs">
                        Not scored
                      </Text>
                    </AnimatedPressable>
                  ) : deal ? (() => {
                    // Unreachable: the branch above already handles a null
                    // score. Written as a guard rather than a cast so the
                    // compiler proves it instead of being told to trust us.
                    const score = deal.readinessScore;
                    if (score == null) return null;
                    const band =
                      deal.scoreEvent?.composite?.ventureLabel ?? scoreToLabel(score);
                    const tint = labelColor(band);
                    return (
                    <AnimatedPressable
                      onPress={handleViewScore}
                      accessibilityRole="button"
                      accessibilityLabel={`View score: ${score} out of 100, ${capitalizeLabel(band)}`}
                      className="px-3 py-1.5 rounded-full flex-row items-center gap-1.5"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                    >
                      <TrendingUp size={14} color={tint} />
                      <Text style={{ color: tint }} className="font-bold">
                        {score}
                      </Text>
                      <Text style={{ color: tint }} className="font-semibold text-xs">
                        · {capitalizeLabel(band)}
                      </Text>
                    </AnimatedPressable>
                    );
                  })() : portfolioCompany ? (
                    <View
                      className="px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                    >
                      <Text style={{ color: colors.success }} className="font-bold">
                        {portfolioCompany.returns ? `${portfolioCompany.returns}x` : 'Invested'}
                      </Text>
                    </View>
                  ) : isDirectorySource ? (
                    <View
                      className="px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: '#FFF4E6' }}
                    >
                      <Text style={{ color: '#FF9500' }} className="font-bold text-xs">
                        UNCLAIMED
                      </Text>
                    </View>
                  ) : null}
                </View>

                <Text className="text-white text-2xl font-bold mb-2">
                  {startup.company.name}
                </Text>
                <Text className="text-white text-sm mb-4 opacity-90">
                  {startup.company.tagline}
                </Text>

                <View className="flex-row flex-wrap gap-2">
                  <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.25)' }} className="px-3 py-1.5 rounded-full">
                    <Text className="text-white text-xs font-medium">
                      {startup.company.industry}
                    </Text>
                  </View>
                  <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.25)' }} className="px-3 py-1.5 rounded-full">
                    <Text className="text-white text-xs font-medium">
                      {stageLabels[startup.company.stage]}
                    </Text>
                  </View>
                  {(startup.funding.currentRaise?.amount ?? 0) > 0 && (
                    <View style={{ backgroundColor: 'rgba(255, 255, 255, 0.25)' }} className="px-3 py-1.5 rounded-full flex-row items-center gap-1">
                      <DollarSign size={12} color="white" />
                      <Text className="text-white text-xs font-medium">
                        {formatCurrency(startup.funding.currentRaise?.amount ?? 0)}
                      </Text>
                    </View>
                  )}
                </View>
              </LinearGradient>
            </Animated.View>

            {/* Quick Info */}
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="px-5 mb-6"
            >
              <View className="flex-row gap-3">
                {startup.company.location ? (
                  <View
                    className="flex-1 rounded-2xl p-4"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <View className="flex-row items-center gap-2 mb-1">
                      <MapPin size={14} color={colors.text.tertiary} />
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">Location</Text>
                    </View>
                    <Text style={{ color: colors.text.primary }} className="font-medium">{startup.company.location.split(',')[0]}</Text>
                  </View>
                ) : null}
                {startup.company.founded ? (
                  <View
                    className="flex-1 rounded-2xl p-4"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <View className="flex-row items-center gap-2 mb-1">
                      <Calendar size={14} color={colors.text.tertiary} />
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">Founded</Text>
                    </View>
                    <Text style={{ color: colors.text.primary }} className="font-medium">{startup.company.founded}</Text>
                  </View>
                ) : null}
                {startup.team.size ? (
                  <View
                    className="flex-1 rounded-2xl p-4"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <View className="flex-row items-center gap-2 mb-1">
                      <Users size={14} color={colors.text.tertiary} />
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">Team</Text>
                    </View>
                    <Text style={{ color: colors.text.primary }} className="font-medium">{startup.team.size} people</Text>
                  </View>
                ) : null}
              </View>
            </Animated.View>

            {/* AI Analysis Confidence - only for pitch deck analyzed startups */}
            {isAIAnalyzed && (
              <Animated.View
                entering={FadeInUp.delay(325).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl p-4"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  <View className="flex-row items-center justify-between">
                    <View className="flex-row items-center gap-2">
                      <View
                        className="w-8 h-8 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.primary[100] }}
                      >
                        <Zap size={16} color={colors.primary[600]} />
                      </View>
                      <View>
                        <Text style={{ color: colors.text.primary }} className="font-medium text-sm">
                          AI-Analyzed Startup
                        </Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">
                          Data extracted from pitch deck
                        </Text>
                      </View>
                    </View>
                    {extractionConfidence !== null && (
                      <View className="flex-row items-center gap-2">
                        <View
                          className="px-3 py-1.5 rounded-full flex-row items-center gap-1.5"
                          style={{
                            backgroundColor: extractionConfidence >= 70
                              ? colors.traffic.greenLight
                              : extractionConfidence >= 50
                                ? colors.amber.light
                                : colors.red.light,
                          }}
                        >
                          <Info size={12} color={
                            extractionConfidence >= 70
                              ? colors.success
                              : extractionConfidence >= 50
                                ? colors.warning
                                : colors.error
                          } />
                          <Text
                            className="text-xs font-semibold"
                            style={{
                              color: extractionConfidence >= 70
                                ? colors.success
                                : extractionConfidence >= 50
                                  ? colors.warning
                                  : colors.error,
                            }}
                          >
                            {extractionConfidence}% confidence
                          </Text>
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              </Animated.View>
            )}

            {/* Directory-specific: Company Registry Info */}
            {isDirectorySource && directoryEntry && (
              <Animated.View
                entering={FadeInUp.delay(350).duration(500)}
                className="px-5 mb-6"
              >
                <View className="px-0 mb-1">
                  <SectionDivider label="Company Registry" marginTop={8} marginBottom={12} />
                </View>
                <View
                  className="rounded-2xl overflow-hidden"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  {directoryEntry.che_number && (
                    <View
                      className="flex-row items-center justify-between p-4 border-b"
                      style={{ borderColor: colors.border.light }}
                    >
                      <Text style={{ color: colors.text.secondary }} className="text-sm">CHE Number</Text>
                      <Text style={{ color: colors.text.primary }} className="font-medium text-sm">{directoryEntry.che_number}</Text>
                    </View>
                  )}
                  {directoryEntry.legal_form && (
                    <View
                      className="flex-row items-center justify-between p-4 border-b"
                      style={{ borderColor: colors.border.light }}
                    >
                      <Text style={{ color: colors.text.secondary }} className="text-sm">Legal Form</Text>
                      <Text style={{ color: colors.text.primary }} className="font-medium text-sm">{directoryEntry.legal_form}</Text>
                    </View>
                  )}
                  {directoryEntry.registry_status && (
                    <View
                      className="flex-row items-center justify-between p-4 border-b"
                      style={{ borderColor: colors.border.light }}
                    >
                      <Text style={{ color: colors.text.secondary }} className="text-sm">Registry Status</Text>
                      <View
                        className="px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: directoryEntry.registry_status === 'ACTIVE' ? `${colors.success}20` : `${colors.warning}20` }}
                      >
                        <Text
                          className="text-xs font-medium"
                          style={{ color: directoryEntry.registry_status === 'ACTIVE' ? colors.success : colors.warning }}
                        >
                          {directoryEntry.registry_status}
                        </Text>
                      </View>
                    </View>
                  )}
                  {directoryEntry.canton && (
                    <View
                      className="flex-row items-center justify-between p-4"
                      style={{ borderColor: colors.border.light }}
                    >
                      <Text style={{ color: colors.text.secondary }} className="text-sm">Canton</Text>
                      <Text style={{ color: colors.text.primary }} className="font-medium text-sm">{directoryEntry.canton}</Text>
                    </View>
                  )}
                </View>
                {directoryEntry.website && (
                  <AnimatedPressable
                    onPress={() => openExternalUrl(directoryEntry.website, 'StartupDetail.directoryWebsite')}
                    className="flex-row items-center justify-center gap-2 mt-3 py-3 rounded-xl"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Globe size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">Visit Website</Text>
                  </AnimatedPressable>
                )}
              </Animated.View>
            )}

            {/* About — Problem, Solution, Target Customer */}
            {hasDescription && (
              <Animated.View
                entering={FadeInUp.delay(350).duration(500)}
                className="px-5 mb-6"
              >
                <View className="px-0 mb-1">
                  <SectionDivider label="About" marginTop={8} marginBottom={12} />
                </View>

                {startup.company.description && !startup.problemSolution.problem && (
                  <View
                    className="rounded-2xl p-4 mb-3"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <Text style={{ color: colors.text.primary }} className="text-sm leading-relaxed">
                      {startup.company.description}
                    </Text>
                  </View>
                )}

                {startup.problemSolution.problem && (
                  <View
                    className="rounded-2xl p-4 mb-3"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <View className="flex-row items-center gap-2 mb-2">
                      <AlertTriangle size={16} color={colors.error} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold text-sm">Problem</Text>
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                      {startup.problemSolution.problem}
                    </Text>
                  </View>
                )}

                {startup.problemSolution.solution && (
                  <View
                    className="rounded-2xl p-4 mb-3"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <View className="flex-row items-center gap-2 mb-2">
                      <CheckCircle size={16} color={colors.success} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold text-sm">Solution</Text>
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                      {startup.problemSolution.solution}
                    </Text>
                  </View>
                )}

                {startup.targetCustomer && (
                  <View
                    className="rounded-2xl p-4"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <View className="flex-row items-center gap-2 mb-2">
                      <Users size={16} color={colors.primary[600]} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold text-sm">Target Customer</Text>
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
                      {startup.targetCustomer}
                    </Text>
                  </View>
                )}
              </Animated.View>
            )}

            {/* Strategic analysis — after the complete About block (description,
                problem, solution, target customer) and before Competitive
                Landscape, so the position does not move with whichever About
                fields this startup happens to have filled in.

                An investor sees a report or "not available yet". Never a
                readiness state, a missing field or a question: those describe
                founder-private material about a company that is not theirs. */}
            {strategicAnalysisEnabled && startupProfileId && (
              <Animated.View entering={FadeInUp.delay(370).duration(500)} className="px-5 mb-6">
                <StrategicAnalysisEntry
                  canEdit={false}
                  {...strategicEntry}
                  onOpen={() => router.push(`/strategic-analysis?startupId=${startupProfileId}` as never)}
                />
              </Animated.View>
            )}

            {/* Competitors */}
            {hasCompetitors && (
              <Animated.View
                entering={FadeInUp.delay(375).duration(500)}
                className="px-5 mb-6"
              >
                <View className="px-0 mb-1">
                  <SectionDivider label="Competitive Landscape" marginTop={8} marginBottom={12} />
                </View>
                <View className="flex-row flex-wrap gap-2">
                  {startup.market.competitors.map((competitor, idx) => (
                    <AnimatedPressable
                      key={idx}
                      onPress={() => {
                        const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(competitor.name)}`;
                        openExternalUrl(searchUrl, 'StartupDetail.competitorSearch');
                      }}
                      className="px-3 py-2 rounded-xl flex-row items-center gap-1.5"
                      style={{ backgroundColor: colors.surface.primary }}
                    >
                      <Text style={{ color: colors.primary[600] }} className="text-sm">{competitor.name}</Text>
                      <ExternalLink size={12} color={colors.text.tertiary} />
                    </AnimatedPressable>
                  ))}
                </View>
              </Animated.View>
            )}

            {/* Traction Metrics — real data from extraction */}
            {realMetrics.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(400).duration(500)}
                className="px-5 mb-6"
              >
                <View className="px-0 mb-1">
                  <SectionDivider label="Traction" marginTop={8} marginBottom={12} />
                </View>
                <View
                  className="rounded-2xl overflow-hidden"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  {realMetrics.map((metric, index) => {
                    const MetricIcon = metric.icon;
                    return (
                      <View
                        key={index}
                        className={`flex-row items-center justify-between p-4 ${
                          index < realMetrics.length - 1 ? 'border-b' : ''
                        }`}
                        style={{ borderColor: colors.border.light }}
                      >
                        <View className="flex-row items-center gap-2">
                          <MetricIcon size={16} color={colors.text.tertiary} />
                          <Text style={{ color: colors.text.secondary }} className="text-sm">
                            {metric.label}
                          </Text>
                        </View>
                        <Text style={{ color: colors.text.primary }} className="font-semibold">{metric.value}</Text>
                      </View>
                    );
                  })}
                </View>
              </Animated.View>
            )}

            {/* Founder & Contact Info */}
            {hasFounderInfo && (
              <Animated.View
                entering={FadeInUp.delay(425).duration(500)}
                className="px-5 mb-6"
              >
                <View className="px-0 mb-1">
                  <SectionDivider label="Founder & Contact" marginTop={8} marginBottom={12} />
                </View>
                <View
                  className="rounded-2xl overflow-hidden"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  {founderPerson?.name && (
                    <View
                      className="flex-row items-center gap-3 p-4 border-b"
                      style={{ borderColor: colors.border.light }}
                    >
                      <View
                        className="w-10 h-10 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <Users size={18} color={colors.primary[600]} />
                      </View>
                      <View className="flex-1">
                        <Text style={{ color: colors.text.primary }} className="font-medium">{founderPerson.name}</Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs">Founder</Text>
                      </View>
                    </View>
                  )}
                  {startup.founderEmail && (
                    <AnimatedPressable
                      onPress={() => openExternalUrl(`mailto:${startup.founderEmail}`, 'StartupDetail.founderEmail')}
                      className="flex-row items-center gap-3 p-4 border-b"
                      style={{ borderColor: colors.border.light }}
                    >
                      <Mail size={18} color={colors.text.tertiary} />
                      <Text style={{ color: colors.primary[600] }} className="text-sm">{startup.founderEmail}</Text>
                      <ExternalLink size={14} color={colors.text.tertiary} />
                    </AnimatedPressable>
                  )}
                  {founderPerson?.linkedIn && (
                    <AnimatedPressable
                      onPress={() => openExternalUrl(founderPerson.linkedIn, 'StartupDetail.founderLinkedIn')}
                      className="flex-row items-center gap-3 p-4 border-b"
                      style={{ borderColor: colors.border.light }}
                    >
                      <Linkedin size={18} color="#0A66C2" />
                      <Text style={{ color: colors.primary[600] }} className="text-sm flex-1" numberOfLines={1}>LinkedIn Profile</Text>
                      <ExternalLink size={14} color={colors.text.tertiary} />
                    </AnimatedPressable>
                  )}
                  {startup.company.website && (
                    <AnimatedPressable
                      onPress={() => openExternalUrl(startup.company.website, 'StartupDetail.companyWebsite')}
                      className="flex-row items-center gap-3 p-4"
                    >
                      <Globe size={18} color={colors.text.tertiary} />
                      <Text style={{ color: colors.primary[600] }} className="text-sm flex-1" numberOfLines={1}>{startup.company.website}</Text>
                      <ExternalLink size={14} color={colors.text.tertiary} />
                    </AnimatedPressable>
                  )}
                </View>
              </Animated.View>
            )}

            {/* View Detailed Score CTA — nudge to score detail page */}
            {deal && (
              <Animated.View
                entering={FadeInUp.delay(450).duration(500)}
                className="px-5 mb-6"
              >
                <AnimatedPressable
                  onPress={handleViewScore}
                  accessibilityRole="button"
                  accessibilityLabel={`View detailed score breakdown, ${deal.readinessScore} out of 100`}
                  className="rounded-2xl p-4 flex-row items-center justify-between"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  {/* `flex-1` + `min-w-0`: without them this block claims its
                      intrinsic width — the full untruncated headline — and shoves
                      the score pill and chevron past the card's right edge. In
                      production the chevron rendered OUTSIDE the card entirely
                      (2026-08-09). The right cluster is `shrink-0` because a
                      score is not something to truncate. */}
                  <View className="flex-row items-center gap-3 flex-1 min-w-0">
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center shrink-0"
                      style={{ backgroundColor: colors.primary[100] }}
                    >
                      <TrendingUp size={20} color={colors.primary[600]} />
                    </View>
                    <View className="flex-1 min-w-0">
                      <Text
                        style={{ color: colors.text.primary }}
                        className="font-semibold text-sm"
                        numberOfLines={1}
                      >
                        View Detailed Score Breakdown
                      </Text>
                      <Text
                        style={{ color: colors.text.tertiary }}
                        className="text-xs"
                        numberOfLines={1}
                      >
                        15-dimension AI analysis with evidence levels
                      </Text>
                    </View>
                  </View>
                  <View className="flex-row items-center gap-1.5 shrink-0 pl-2">
                    <IRSScorePill
                      score={deal.readinessScore}
                      ventureLabel={deal.scoreEvent?.composite?.ventureLabel}
                      showIcon={false}
                    />
                    <ChevronRight size={18} color={colors.primary[600]} />
                  </View>
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* Section Divider - News & Updates */}
            {visibleUpdates.length > 0 && (
              <View className="px-5">
                <SectionDivider label="News & Updates" icon={Newspaper} marginTop={8} marginBottom={12} />
              </View>
            )}

            {/* News & Updates */}
            {visibleUpdates.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(475).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl overflow-hidden"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  {visibleUpdates
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((update, index) => (
                      <AnimatedPressable
                        key={update.id}
                        onPress={() => setSelectedUpdate(update)}
                        className={`flex-row items-center p-4 ${
                          index < visibleUpdates.length - 1 ? 'border-b' : ''
                        }`}
                        style={{ borderColor: colors.border.light }}
                      >
                        <View
                          className="w-10 h-10 rounded-full items-center justify-center mr-3"
                          style={{ backgroundColor: getUpdateBgColor(update.type) }}
                        >
                          {getUpdateIcon(update.type)}
                        </View>
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
                            {update.title}
                          </Text>
                          <View className="flex-row items-center gap-2 mt-0.5">
                            <Clock size={12} color={colors.text.tertiary} />
                            <Text style={{ color: colors.text.tertiary }} className="text-xs">
                              {formatDate(update.date)}
                            </Text>
                            {update.externalLink && (
                              <>
                                <Text style={{ color: colors.text.tertiary }} className="text-xs">•</Text>
                                <ExternalLink size={12} color={colors.primary[600]} />
                              </>
                            )}
                          </View>
                        </View>
                        <ChevronRight size={20} color={colors.light[400]} />
                      </AnimatedPressable>
                    ))}
                </View>
              </Animated.View>
            )}

            {/* Invite Founder - for unclaimed startups (dealflow or directory) */}
            {isUnclaimed && (
              <Animated.View
                entering={FadeInUp.delay(490).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl p-5"
                  style={{ backgroundColor: colors.amber.light }}
                >
                  <View className="flex-row items-center gap-3 mb-3">
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center"
                      style={{ backgroundColor: `${colors.warning}25` }}
                    >
                      <UserPlus size={20} color={colors.warning} />
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: colors.text.primary }} className="text-base font-semibold">
                        Unclaimed Profile
                      </Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">
                        No founder connected
                      </Text>
                    </View>
                  </View>
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm mb-4"
                  >
                    {isDirectorySource
                      ? 'This profile was imported from the company registry. Invite the founder to claim their page and unlock full due diligence.'
                      : 'This startup profile was created from a pitch deck analysis. Invite the founder to claim their page and connect their data room for complete due diligence.'}
                  </Text>
                  <AnimatedPressable
                    onPress={() => {
                      // Pre-populate with extracted founder email if available
                      if (startup?.founderEmail) {
                        setInviteEmail(startup.founderEmail);
                      }
                      setShowInviteModal(true);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Invite founder to claim this profile"
                    className="rounded-xl py-3.5 flex-row items-center justify-center gap-2"
                    style={{ backgroundColor: colors.warning }}
                  >
                    <Mail size={18} color="white" />
                    <Text className="text-white font-semibold">Invite Founder to Claim</Text>
                  </AnimatedPressable>
                </View>
              </Animated.View>
            )}

            {/* Message the founder — appears once the investor has actually
                signalled interest. Same threshold the conversations INSERT
                policy enforces; this is the affordance, not the permission. */}
            {deal && (
              <Animated.View
                entering={FadeInUp.delay(450).duration(500)}
                className="px-5 mb-4"
              >
                <MessageFounderButton
                  startupId={deal.startup.id}
                  startupName={startup.company.name}
                  investorProfileId={investorProfile?.id ?? null}
                  dealflowStatus={deal.status}
                  // NOT `documentsOpen`. That answers "are the documents open
                  // right now" (the stage); this answers "has this founder
                  // granted me access" (their `data_room_access` row). The two
                  // look interchangeable and are not — a concluded deal keeps
                  // the grant, and the server's Door A clause 2 honours it.
                  dataRoomAccessGranted={dataRoomGranted}
                />
              </Animated.View>
            )}

            {/* Data Room Access - only for dealflow items */}
            {deal && (
              <Animated.View
                entering={FadeInUp.delay(500).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl p-5"
                  style={{
                    backgroundColor: documentsOpen
                      ? colors.traffic.greenLight
                      : isUnclaimed
                        ? colors.surface.tertiary
                        : colors.surface.primary,
                  }}
                >
                  <View className="flex-row items-center gap-3 mb-3">
                    <FolderOpen
                      size={24}
                      color={documentsOpen ? colors.success : colors.text.tertiary}
                    />
                    <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">Data Room</Text>
                    {isUnclaimed && !documentsOpen && (
                      <View
                        className="px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: colors.amber.light }}
                      >
                        <Text style={{ color: colors.warning }} className="text-xs font-medium">
                          Pending
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm mb-4"
                  >
                    {dataRoomState === 'open'
                      ? 'You have access to this startup\'s data room. View pitch deck, financials, and legal documents.'
                      : dataRoomState === 'unclaimed'
                        ? 'Data room access will be available once the founder claims this profile.'
                        : dataRoomState === 'awaiting'
                          ? `${startup.company.name} has been asked to open their data room. Diligence starts once they approve.`
                          : dataRoomState === 'access-rejected'
                            ? `${startup.company.name} declined this request. You can message the founder to discuss it.`
                          : dataRoomState === 'ask-in-bar'
                            ? 'Use "Request access" below to ask for detailed financials, pitch deck, and legal documents.'
                            // `concluded` — an ending. Every LIVE stage lands in
                            // one of the four above, so requesting is not a legal
                            // move from here and the card states the position
                            // rather than inviting one.
                            : 'This deal has been concluded — the data room is not open.'}
                  </Text>
                  {/* One action, one button. The card draws its own CTA only
                      when it is REPORTING something the action bar does not:
                      the documents are open ("View Data Room"), the founder is
                      deciding, or nobody has claimed the profile.

                      It no longer draws when the request is merely illegal.
                      That case used to be indistinguishable from "the bar is
                      offering it" — both were `!requestOfferedInActionBar` —
                      and with `screening` now offering `request-access` the
                      only stages left in the gap are the three endings, where
                      the button asked a founder for documents and then tried to
                      drag a terminal deal back to `awaiting_access`. See
                      `lib/dealflow/data-room-cta.ts`. */}
                  {/* A declined request is the one state whose button is a
                      real action rather than a report: it opens the thread with
                      the founder. Reuses MessageFounderButton so the
                      find-or-create + navigate + gate handling lives in one
                      place — the channel stays open after a decline, because
                      `respond_to_data_room_request` leaves the stage alone and
                      `awaiting_access` is an OPEN_STAGE. Asking again is not
                      offered: a rejection is final and RLS refuses it. */}
                  {dataRoomState === 'access-rejected' && (
                    <MessageFounderButton
                      startupId={startupProfileId ?? ''}
                      startupName={startup.company.name}
                      investorProfileId={investorProfile?.id ?? null}
                      dealflowStatus={deal?.status ?? null}
                      dataRoomAccessGranted={dataRoomGranted}
                    />
                  )}
                  {dataRoomState !== 'access-rejected' && cardDrawsOwnCta(dataRoomState) && (
                  <AnimatedPressable
                    onPress={
                      dataRoomState === 'open'
                        ? () => { track('data_room_accessed', { startupId: id }); router.push(id ? `/duediligence/${id}` : '/(investor)/duediligence'); }
                        : undefined
                    }
                    className="rounded-xl py-3.5"
                    style={{
                      backgroundColor: dataRoomState === 'open'
                        ? colors.success
                        : colors.light[400],
                    }}
                    // Every state that still draws a button other than `open`
                    // is a REPORT, not an offer: there is nothing to tap. No
                    // `isRequesting` spinner either — a request in flight puts
                    // the card in `ask-in-bar`, where it draws nothing, and the
                    // action bar's own button owns that round trip.
                    disabled={dataRoomState !== 'open'}
                  >
                    <Text className="text-white font-semibold text-center">
                      {dataRoomState === 'open'
                        ? 'View Data Room'
                        : dataRoomState === 'unclaimed'
                          ? 'Awaiting Founder'
                          : 'Awaiting Founder Approval'}
                    </Text>
                  </AnimatedPressable>
                  )}
                </View>
              </Animated.View>
            )}

            {/* Portfolio Investment Info - only for portfolio items */}
            {isPortfolioView && portfolioCompany && (
              <Animated.View
                entering={FadeInUp.delay(500).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl p-5"
                  style={{ backgroundColor: colors.traffic.greenLight }}
                >
                  <View className="flex-row items-center justify-between mb-3">
                    <View className="flex-row items-center gap-3">
                      <DollarSign size={24} color={colors.success} />
                      <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">Your Investment</Text>
                    </View>
                    <Pressable
                      onPress={() => setIsMasked(!isMasked)}
                      accessibilityRole="switch"
                      accessibilityLabel={isMasked ? 'Show investment details' : 'Hide investment details'}
                      accessibilityState={{ checked: !isMasked }}
                      className="p-2 rounded-lg active:opacity-70"
                      style={{ backgroundColor: 'rgba(0,0,0,0.06)' }}
                      hitSlop={8}
                    >
                      {isMasked ? (
                        <EyeOff size={16} color={colors.text.tertiary} />
                      ) : (
                        <Eye size={16} color={colors.success} />
                      )}
                    </Pressable>
                  </View>
                  <View className="flex-row justify-between mb-2">
                    <Text style={{ color: colors.text.secondary }} className="text-sm">Invested</Text>
                    <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                      {isMasked ? '••••••' : formatCurrency(portfolioCompany.investmentAmount)}
                    </Text>
                  </View>
                  <View className="flex-row justify-between mb-2">
                    <Text style={{ color: colors.text.secondary }} className="text-sm">Equity</Text>
                    <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                      {isMasked ? '••••' : `${portfolioCompany.equity}%`}
                    </Text>
                  </View>
                  <View className="flex-row justify-between mb-2">
                    <Text style={{ color: colors.text.secondary }} className="text-sm">Current Valuation</Text>
                    <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                      {isMasked ? '••••••' : formatCurrency(portfolioCompany.currentValuation)}
                    </Text>
                  </View>
                  {portfolioCompany.returns && (
                    <View className="flex-row justify-between">
                      <Text style={{ color: colors.text.secondary }} className="text-sm">Returns</Text>
                      <Text style={{ color: isMasked ? colors.text.tertiary : colors.success }} className="text-sm font-bold">
                        {isMasked ? '••••' : `${portfolioCompany.returns}x`}
                      </Text>
                    </View>
                  )}
                </View>
              </Animated.View>
            )}

            {/* Notes - only for dealflow items */}
            {deal?.notes && (
              <Animated.View
                entering={FadeInUp.delay(550).duration(500)}
                className="px-5 mb-6"
              >
                <View
                  className="rounded-2xl p-4"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <View className="flex-row items-center gap-2 mb-2">
                    <MessageSquare size={16} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                      Your Notes
                    </Text>
                  </View>
                  <Text style={{ color: colors.text.secondary }} className="text-sm italic">
                    "{deal.notes}"
                  </Text>
                </View>
              </Animated.View>
            )}

            {/* Spacer for bottom actions */}
            <View className="h-24" />
          </ScrollView>

          {/* Fixed Bottom Actions - different for dealflow vs portfolio vs directory */}
          {deal ? (
            dealActions.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(600).duration(500)}
                className="absolute bottom-0 left-0 right-0 px-5 pt-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderTopWidth: 1,
                  borderTopColor: colors.border.light,
                  // The content SafeAreaView only claims the top edge, so this
                  // absolute bar must clear the home indicator itself.
                  paddingBottom: insets.bottom + 12,
                }}
              >
                <View className="flex-row flex-wrap gap-3">
                  {dealActions.map((action) => {
                    const presentation = CARD_ACTION_PRESENTATION[action];
                    // The round trip belongs to the button that started it. The
                    // Data Room card's spinner used to own this, but that card
                    // yields to this bar while a request is offerable — so it is
                    // hidden exactly when the feedback is needed.
                    const pending = action === 'request-access' && isRequesting;
                    return (
                      <CardActionButton
                        key={action}
                        label={pending ? 'Requesting…' : presentation.label}
                        variant={presentation.variant}
                        icon={presentation.icon ? CARD_ACTION_ICONS[presentation.icon] : undefined}
                        disabled={pending}
                        accessibilityLabel={`${presentation.label} — ${startup.company.name}`}
                        onPress={() => handleDealAction(action)}
                      />
                    );
                  })}
                </View>
              </Animated.View>
            )
          ) : (
            <Animated.View
              entering={FadeInUp.delay(600).duration(500)}
              className="absolute bottom-0 left-0 right-0 px-5 pt-4"
              style={{
                backgroundColor: colors.surface.primary,
                borderTopWidth: 1,
                borderTopColor: colors.border.light,
                // The content SafeAreaView only claims the top edge, so this
                // absolute bar must clear the home indicator itself.
                paddingBottom: insets.bottom + 12,
              }}
            >
              <AnimatedPressable
                onPress={() => router.push(isDirectorySource ? '/company-directory' : '/(investor)/portfolio')}
                accessibilityRole="button"
                accessibilityLabel={isDirectorySource ? 'Back to directory' : 'Back to portfolio'}
                className="rounded-xl py-4 items-center justify-center"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Text className="text-white font-semibold">
                  {isDirectorySource ? 'Back to Directory' : 'Back to Portfolio'}
                </Text>
              </AnimatedPressable>
            </Animated.View>
          )}
        </SafeAreaView>
      </View>
  );

  // Update Detail Modal (rendered separately for both mobile and web)
  const updateModal = (
    <Modal
          visible={!!selectedUpdate}
          animationType={isWeb ? 'fade' : 'slide'}
          presentationStyle={isWeb ? undefined : 'pageSheet'}
          transparent={isWeb}
          onRequestClose={() => setSelectedUpdate(null)}
        >
          <View style={isWeb ? { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' } : { flex: 1 }}>
          <View style={isWeb ? { width: '100%', maxWidth: 560, maxHeight: '85%', borderRadius: 16, overflow: 'hidden' } : { flex: 1 }}>
          <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
            {selectedUpdate && (
              <>
                {/* Modal Header */}
                <View
                  className="flex-row items-center justify-between px-5 py-4"
                  style={{ backgroundColor: colors.surface.primary, borderBottomWidth: 1, borderBottomColor: colors.border.light }}
                >
                  <Pressable
                    onPress={() => setSelectedUpdate(null)}
                    accessibilityRole="button"
                    accessibilityLabel="Close update details"
                    className="w-10 h-10 rounded-full items-center justify-center active:opacity-70"
                    style={{ backgroundColor: colors.surface.secondary }}
                  >
                    <X size={24} color={colors.text.primary} />
                  </Pressable>
                  <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                    Update Details
                  </Text>
                  <View className="w-10" />
                </View>

                {/* Modal Content */}
                <ScrollView className="flex-1" contentContainerStyle={{ padding: 20 }}>
                  {/* Update Type Badge */}
                  <View className="flex-row items-center gap-3 mb-4">
                    <View
                      className="w-12 h-12 rounded-2xl items-center justify-center"
                      style={{ backgroundColor: getUpdateBgColor(selectedUpdate.type) }}
                    >
                      {getUpdateIcon(selectedUpdate.type)}
                    </View>
                    <View
                      className="px-3 py-1 rounded-full"
                      style={{ backgroundColor: colors.primary[50] }}
                    >
                      <Text style={{ color: colors.primary[600] }} className="text-xs font-medium capitalize">
                        {selectedUpdate.type}
                      </Text>
                    </View>
                  </View>

                  {/* Title */}
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-2">
                    {selectedUpdate.title}
                  </Text>

                  {/* Date and Author */}
                  <View className="flex-row items-center gap-4 mb-6">
                    <View className="flex-row items-center gap-1">
                      <Clock size={14} color={colors.text.tertiary} />
                      <Text style={{ color: colors.text.tertiary }} className="text-sm">
                        {formatDate(selectedUpdate.date)}
                      </Text>
                    </View>
                    <View className="flex-row items-center gap-1">
                      <Users size={14} color={colors.text.tertiary} />
                      <Text style={{ color: colors.text.tertiary }} className="text-sm">
                        {selectedUpdate.createdBy}
                      </Text>
                    </View>
                  </View>

                  {/* Summary */}
                  <View
                    className="rounded-2xl p-5 mb-6"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <Text style={{ color: colors.text.secondary }} className="text-base leading-6">
                      {selectedUpdate.summary}
                    </Text>
                  </View>

                  {/* External Link */}
                  {selectedUpdate.externalLink && (
                    <AnimatedPressable
                      onPress={() => openExternalUrl(selectedUpdate.externalLink, 'StartupDetail.updateExternalLink')}
                      className="flex-row items-center justify-center gap-2 py-4 rounded-xl"
                      style={{ backgroundColor: colors.primary[600] }}
                    >
                      <ExternalLink size={20} color="white" />
                      <Text className="text-white font-semibold">Read More</Text>
                    </AnimatedPressable>
                  )}

                  {/* Company Info */}
                  <View
                    className="rounded-2xl p-4 mt-6"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <Text style={{ color: colors.text.tertiary }} className="text-xs uppercase tracking-wider mb-2">
                      Posted by
                    </Text>
                    <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">
                      {startup.company.name}
                    </Text>
                    <Text style={{ color: colors.text.secondary }} className="text-sm">
                      {startup.company.tagline}
                    </Text>
                  </View>
                </ScrollView>
              </>
            )}
          </SafeAreaView>
          </View>
          </View>
        </Modal>
  );

  // Invite Founder Modal
  const inviteModal = (
    <InviteFounderModal
      visible={showInviteModal}
      onClose={() => {
        setShowInviteModal(false);
        setInviteEmail('');
      }}
      startupName={startup.company.name}
      founderName={founderPerson?.name}
      founderEmail={startup?.founderEmail}
      inviteEmail={inviteEmail}
      onSetInviteEmail={setInviteEmail}
      inviteSending={inviteSending}
      onSubmit={handleInviteFounder}
    />
  );

  if (isWeb) {
    return (
      <AppLayout title={startup.company.name} breadcrumbs={breadcrumbs}>
        {content}
        {updateModal}
        {inviteModal}
      </AppLayout>
    );
  }

  return (
    <>
      {content}
      {updateModal}
      {inviteModal}
    </>
  );
}
